<script lang="ts" setup>
	import { useUserMedia, useDevicesList } from '@vueuse/core'
	import { useResourcesApi } from '~/services/apis/resources'

	export type mediaType = {
		blob: null | Blob
		src: null | string
	}

	const props = defineProps<{
		value: string | null
		file?: mediaType
		tip?: string
		serverUpload?: boolean
	}>()

	const currentCamera = ref<string>()
	const video = ref<HTMLVideoElement>()
	const canvas = ref<HTMLCanvasElement>()
	const loading = shallowRef(false)

	const { stream, enabled, stop, start, isSupported } = useUserMedia({
		constraints: { video: { deviceId: currentCamera.value } }
	})
	const emits = defineEmits<{
		(event: 'update:value', param: string): void
		(event: 'update:file', param: mediaType): void
	}>()

	const openCamera = () => {
		if (!isSupported.value) {
			return useNotification().alert({
				status: 400,
				message: 'Sorry your browser does not support this feature, use upload'
			})
		}
		loading.value = true
		const { videoInputs: cameras } = useDevicesList({
			requestPermissions: true,
			constraints: {
				video: true,
				audio: false
			},
			onUpdated() {
				if (!cameras.value.find(i => i.deviceId === currentCamera.value))
					currentCamera.value = cameras.value[0]?.deviceId
			}
		})
		start().finally(() => (loading.value = false))
	}

	const capture = () => {
		if (!canvas.value || !video.value) {
			console.error('Failed to capture canvas')
			useNotification().alert({
				status: 400,
				message: 'Something went wrong, can not capture image.'
			})
			return null
		}
		const context = canvas.value.getContext('2d')
		// The values provided are required image style props
		context?.drawImage(video.value, 0, 0, video.value.width, video.value.height)

		try {
			canvas.value.toBlob(
				async blob => {
					if (!blob) {
						return null
					}
					const url = URL.createObjectURL(blob)
					const photo_name = Date.now()
					const fileResult = new File([blob], `${photo_name}.jpg`, { type: 'image/jpg' })
					stop()

					if (props.serverUpload) {
						loading.value = true
						const { uploadFileApi } = useResourcesApi()
						const dataInstance = new FormData()
						dataInstance.append('file', fileResult)
						const fileResponse = await uploadFileApi(dataInstance)

						if (fileResponse.value?.data?.file_url) {
							emits('update:file', {
								blob: fileResult,
								src: fileResponse.value.data.file_url
							})
							emits('update:value', fileResponse.value.data.file_url)
						}
					} else {
						emits('update:file', {
							blob: fileResult,
							src: url
						})
						emits('update:value', url)
					}
				},
				'image/jpeg',
				0.7
			)
		} catch (error) {
			useNotification().alert({
				status: 400,
				message: error as string
			})
		} finally {
			loading.value = false
		}
	}

	const retakePhoto = () => {
		emits('update:file', {
			blob: null,
			src: null
		})
		emits('update:value', '')
		openCamera()
	}

	onUnmounted(() => {
		stop()
		enabled.value = false
	})

	watchEffect(() => {
		if (video.value) {
			video.value.srcObject = stream.value!
		}
	})
</script>

<template>
	<div>
		<media-placeholder
			:is-empty="(file?.src === null || !value) && !enabled"
			icon-name="camera"
			icon-dir="media"
			caption="Tap to take a selfie"
			@open="openCamera"
		>
			<template #empty>
				<div v-if="loading" class="spin absolute mx-auto">
					<a-spin size="large" />
				</div>
			</template>
			<video v-if="!value && !file?.src" ref="video" :width="400" :height="280" muted autoplay />
			<img
				v-else
				alt="selfie"
				:src="value || (file?.src as string)"
				class="w-full rounded-lg object-cover"
				loading="lazy"
			/>
			<canvas ref="canvas" :width="400" :height="280" class="absolute top-0 opacity-0" />

			<button
				v-if="enabled"
				name="capture image"
				type="button"
				class="absolute left-0 right-0 z-10 mx-auto inline-flex h-[60px] w-[60px] flex-none -translate-y-16 items-center justify-center rounded-full bg-white bg-opacity-90 transition-all hover:bg-opacity-100"
				@click="capture"
			>
				<lazy-tools-icon icon="camera" dir="media" :height="25" :width="25" />
			</button>
		</media-placeholder>

		<div v-if="value || file?.src" class="flex justify-center">
			<button
				type="button"
				class="default-btn primary-border group mb-12 mt-8 w-full max-w-[218px] justify-center gap-x-2 rounded-lg font-medium"
				@click="retakePhoto"
			>
				<div
					class="flex h-[24px] w-[24px] flex-none items-center justify-center rounded-lg border border-primary-2 group-hover:border-white"
				>
					<lazy-tools-icon
						dir="other"
						icon="renew-plan"
						:height="13"
						:width="13"
						path-class="group-hover:fill-white fill-primary-2 "
					/>
				</div>
				Change Photo
			</button>
		</div>
		<media-tip
			v-show="!value || !file?.src"
			class="mt-6"
			:note="tip || 'Take photo in good lighting and make sure your face takes up to 75% of the screen.'"
		/>
	</div>
</template>

<style scoped>
	.spin :deep(.ant-spin-dot-item) {
		@apply bg-[#506661];
	}
</style>
